import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, MapPin } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { fetchListings } from '@/lib/marketplace';
import { Listing } from '@/components/JobCard';
import { tradeLabel, localizedSlug } from '@/data/constants';
import { coverImageFor } from '@/lib/coverImage';
import { locationLabel } from '@/lib/listingSettings';
import CoverImage from '@/components/CoverImage';



/**
 * "Current Project Opportunities" block used by every trade / city / county /
 * money page.
 *
 * SECURITY: reads through the rate-limited `browse-listings` edge function
 * only. City, ZIP, address, contacts, notes and document paths are never in
 * the response, so nothing protected can flash before authorization.
 *
 * IMAGES: every card carries a real cover photo resolved by the single
 * resolver in src/lib/coverImage.ts (homeowner upload -> trade cover ->
 * marketplace cover). A project can never render as a blank/gray tile here.
 * No demo or seeded rows exist in this component — everything is live data.
 */
/**
 * `state` is OPTIONAL. Omit it for a nationwide feed (trade pages, project
 * directory, /states index). California hub / county / city / money pages pass
 * state="CA" explicitly; state landing pages pass their own code. State+city
 * landing pages also pass `city` (exact normalised match, server-side).
 */
const LiveProjects: React.FC<{ trade?: string; state?: string; city?: string; heading: string; limit?: number }> = ({
  trade,
  state,
  city,
  heading,
  limit = 6,
}) => {
  const { lang, path, t } = useI18n();
  const browseAll = (() => {
    const qs = new URLSearchParams();
    if (state) qs.set('state', state);
    if (city) qs.set('city', city);
    if (trade) qs.set('trade', trade);
    const s = qs.toString();
    return `${path('/jobs')}${s ? `?${s}` : ''}`;
  })();

  const [rows, setRows] = useState<Listing[] | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    fetchListings({ trade, state, city, cityExact: !!city, limit }).then((r) => {
      if (alive) setRows(r);
    });
    return () => {
      alive = false;
    };
  }, [trade, state, city, limit]);

  if (rows === null) {
    return (
      <section className="mt-10">
        <h2 className="text-xl font-black text-slate-900 sm:text-2xl">{heading}</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-44 animate-pulse rounded-2xl border border-slate-200 bg-slate-100" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10">
      <h2 className="text-xl font-black text-slate-900 sm:text-2xl">{heading}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
          {t('seo.noProjects')}{' '}
          <Link to={browseAll} className="font-bold text-orange-600 hover:underline">
            {t('seo.ctaBrowse')}
          </Link>
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((l) => {
              const cover = coverImageFor(l);
              return (
                <Link
                  key={l.id}
                  to={path(`/jobs/${localizedSlug(l.slug, l.trade, lang)}`)}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md"
                >
                  <CoverImage listing={l} alt={cover.isRealPhoto ? l.title : ''} aspect="aspect-[16/9]">
                    {!cover.isRealPhoto && (
                      <span className="absolute bottom-2 right-2 rounded-full bg-slate-900/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur">
                        {t('cover.category')}
                      </span>
                    )}
                  </CoverImage>

                  <div className="p-4">
                    <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
                      {tradeLabel(l.trade, lang)}
                    </span>
                    <h3 className="mt-2 line-clamp-2 text-sm font-bold text-slate-900 group-hover:text-orange-600">
                      {l.title}
                    </h3>
                    {/*
                      LOCATION — city + state abbreviation ("Fontana, CA"),
                      falling back to the full state name. The street address,
                      ZIP and homeowner contact stay locked until purchase.
                    */}
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-600">
                      <MapPin className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
                      <span className="font-semibold text-slate-700">
                        {locationLabel({ city: l.city, state: l.state })}
                      </span>
                    </p>
                    <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                      <Lock className="h-3 w-3" aria-hidden /> {t('jobs.exactLocationHidden')}
                    </p>

                    {l.budget_range && (
                      <p className="mt-1 text-xs font-semibold text-slate-700">{l.budget_range}</p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          <Link
            to={browseAll}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 hover:underline"
          >
            {t('seo.seeAll')} <ArrowRight className="h-4 w-4" />
          </Link>
        </>
      )}
    </section>
  );
};

export default LiveProjects;
